// ============================================
// ShadowMapPass — 1D radial shadow maps via compute shader
// ============================================
//
// Collects shadow caster edges, uploads them to GPU, and dispatches a
// compute shader that traces rays from each shadow-casting light to
// produce 1D distance fields (shadow atlas).
//
// Usage:
// ```ts
// const shadowPass = new ShadowMapPass(ctx);
//
// // Per frame, after LightManager.end():
// shadowPass.beginFrame();
// shadowPass.addCaster(caster);
// shadowPass.computeShadows(lightManager);
//
// // Access shadow atlas buffer for lighting shaders:
// shadowPass.shadowMapBuffer;
// ```

import type { IRenderContext } from '../core/IRenderContext';
import type { LightManager } from './LightManager';
import type { ShadowCaster2D } from './ShadowCaster2D';
import { extractEdges } from './ShadowCaster2D';
import type { BufferHandle, ComputePipelineHandle, ShaderHandle } from '../types';
import { NULL_HANDLE } from '../types';
import { defineStruct, GPUType } from '../core/GPUTypes';
import { graphicsConfig } from '../GraphicsConfig';
import { getCompiledShader } from '../pipeline/ShaderCache';

// ============================================================================
// ShadowUniforms struct (matches shadow-map.slang)
// ============================================================================

const SHADOW_UNIFORMS = defineStruct('ShadowUniforms', {
  shadowLightCount: GPUType.u32().build(),
  totalEdges:       GPUType.u32().build(),
  shadowMapRes:     GPUType.u32().build(), // was the SHADOW_MAP_RES override
  _pad1:            GPUType.u32().build(),
});

// ============================================================================
// ShadowMapPass
// ============================================================================

export class ShadowMapPass {
  private ctx: IRenderContext;

  // ---- GPU resources ----
  private computeShader: ShaderHandle = NULL_HANDLE;
  private computePipeline: ComputePipelineHandle = NULL_HANDLE;
  // Second pass: Gaussian-blur the raw 1D distances into 2-moment (μ, E[d²]) data for single-tap soft shadows.
  private blurShader: ShaderHandle = NULL_HANDLE;
  private blurPipeline: ComputePipelineHandle = NULL_HANDLE;
  private _uniformBuffer: BufferHandle = NULL_HANDLE;
  private _edgeBuffer: BufferHandle = NULL_HANDLE;
  private _rawShadowBuffer: BufferHandle = NULL_HANDLE;   // raw per-angle nearest-occluder distance (pass 1)
  private _shadowMapBuffer: BufferHandle = NULL_HANDLE;    // blurred 2-moment buffer bound by lighting shaders
  private _shadowLightIndexBuffer: BufferHandle = NULL_HANDLE;

  // ---- CPU staging ----
  private readonly uniformWriter = SHADOW_UNIFORMS.createWriter();
  private readonly _uniformF32 = new Float32Array(SHADOW_UNIFORMS.size / 4);

  // Pre-allocated edge staging buffer (vec4f per edge = 4 floats)
  private readonly _cpuEdgeData: Float32Array;
  private _edgeCount = 0;

  // Pre-allocated shadow light index staging (u32 per shadow light)
  private readonly _cpuShadowIndices: Uint32Array;

  constructor(ctx: IRenderContext) {
    this.ctx = ctx;

    // Read live config values
    const maxEdges = graphicsConfig.maxShadowEdges;
    const maxShadowLights = graphicsConfig.maxShadowLights;
    const shadowRes = graphicsConfig.shadowMapResolution;

    // Allocate CPU staging
    this._cpuEdgeData = new Float32Array(maxEdges * 4);
    this._cpuShadowIndices = new Uint32Array(maxShadowLights);

    if (!ctx.supportsCompute) {
      console.warn('[ShadowMapPass] Compute not supported — shadows unavailable');
      return;
    }

    // Create compute shader (precompiled Slang → WGSL from the startup cache)
    this.computeShader = ctx.createComputeShader!(getCompiledShader('lighting/shadow-map'));

    // Create compute pipeline
    // Bindings: uniform(0), storage(1)=lights, storage(2)=edges, storage(3)=rawShadow, storage(5)=indices
    this.computePipeline = ctx.createComputePipeline!({
      shader: this.computeShader,
      entryPoint: 'main',
      workgroupSize: [256, 1, 1],
      storageBufferCount: 4,
      label: 'shadow-map-pipeline',
    });

    // Blur pass: raw distances → blurred 2-moment buffer (μ, E[d²]) for single-tap soft shadows in the lit shaders.
    // Bindings: uniform(0), storage(1)=lights, storage(2)=rawShadow, storage(3)=moments, storage(5)=indices
    this.blurShader = ctx.createComputeShader!(getCompiledShader('lighting/shadow-blur'));
    this.blurPipeline = ctx.createComputePipeline!({
      shader: this.blurShader,
      entryPoint: 'main',
      workgroupSize: [256, 1, 1],
      storageBufferCount: 4,
      label: 'shadow-blur-pipeline',
    });

    // Create uniform buffer
    this._uniformBuffer = ctx.createBuffer({
      size: SHADOW_UNIFORMS.size,
      usage: ['uniform', 'copy-dst'],
      label: 'shadow-map-uniforms',
    });

    // Create edge storage buffer (vec4f per edge)
    this._edgeBuffer = ctx.createBuffer({
      size: maxEdges * 16, // 4 floats × 4 bytes
      usage: ['storage', 'copy-dst'],
      label: 'shadow-edge-buffer',
    });

    // Raw shadow distances (pass 1 output): maxShadowLights rows × shadowRes columns × 4 bytes (1 f32/texel).
    this._rawShadowBuffer = ctx.createBuffer({
      size: maxShadowLights * shadowRes * 4,
      usage: ['storage', 'copy-dst'],
      label: 'shadow-raw-buffer',
    });

    // Blurred 2-moment buffer (pass 2 output, bound by lighting shaders): 2 f32/texel (μ, E[d²]).
    this._shadowMapBuffer = ctx.createBuffer({
      size: maxShadowLights * shadowRes * 2 * 4,
      usage: ['storage', 'copy-dst'],
      label: 'shadow-map-buffer',
    });

    // Create shadow light index mapping buffer
    this._shadowLightIndexBuffer = ctx.createBuffer({
      size: maxShadowLights * 4,
      usage: ['storage', 'copy-dst'],
      label: 'shadow-light-indices',
    });
  }

  // -----------------------------------------------------------------------
  // Accessors
  // -----------------------------------------------------------------------

  /** Shadow atlas storage buffer for binding in lighting shaders. */
  get shadowMapBuffer(): BufferHandle { return this._shadowMapBuffer; }

  /** Whether this pass is ready (both the ray-trace and blur pipelines compiled). */
  get isReady(): boolean { return this.computePipeline !== NULL_HANDLE && this.blurPipeline !== NULL_HANDLE; }

  // -----------------------------------------------------------------------
  // Per-Frame API
  // -----------------------------------------------------------------------

  /** Begin a new frame of shadow caster collection. Resets the edge count. */
  beginFrame(): void {
    this._edgeCount = 0;
  }

  /**
   * Add a shadow caster's edges to the per-frame collection.
   * Must be called between beginFrame() and computeShadows().
   */
  addCaster(caster: ShadowCaster2D): void {
    if (!caster.enabled) return;

    const edges = extractEdges(caster.shape);
    const numEdges = edges.length / 4;

    for (let i = 0; i < numEdges && this._edgeCount < graphicsConfig.maxShadowEdges; i++) {
      const base = this._edgeCount * 4;
      this._cpuEdgeData[base + 0] = edges[i * 4 + 0];
      this._cpuEdgeData[base + 1] = edges[i * 4 + 1];
      this._cpuEdgeData[base + 2] = edges[i * 4 + 2];
      this._cpuEdgeData[base + 3] = edges[i * 4 + 3];
      this._edgeCount++;
    }
  }

  /**
   * Dispatch the shadow map compute shader.
   * Must be called after LightManager.end() and all addCaster() calls.
   *
   * @param lightManager - LightManager with finalized light data
   */
  computeShadows(lightManager: LightManager): void {
    if (!this.isReady) return;
    
    const shadowLightCount = lightManager.shadowLightCount;
    if (shadowLightCount === 0) return;

    const ctx = this.ctx;

    // Upload edge data (only used portion)
    if (this._edgeCount > 0) {
      ctx.updateBuffer(
        this._edgeBuffer,
        this._cpuEdgeData.subarray(0, this._edgeCount * 4),
      );
    }

    // Upload shadow light index mapping
    const shadowMap = lightManager.shadowLightMap;
    for (let i = 0; i < shadowLightCount; i++) {
      this._cpuShadowIndices[i] = shadowMap[i];
    }
    ctx.updateBuffer(
      this._shadowLightIndexBuffer,
      this._cpuShadowIndices.subarray(0, shadowLightCount),
    );

    // Upload uniforms
    this.uniformWriter.setAll({
      shadowLightCount,
      totalEdges: this._edgeCount,
      shadowMapRes: graphicsConfig.shadowMapResolution,
      _pad1: 0,
    });
    const src = new Uint8Array(this.uniformWriter.buffer);
    const dst = new Uint8Array(this._uniformF32.buffer);
    dst.set(src);
    ctx.updateBuffer(this._uniformBuffer, this._uniformF32);

    // Dispatch: (ceil(RESOLUTION/256), shadowLightCount, 1)
    // Each workgroup processes 256 angle samples; 512 resolution needs 2 workgroups per light
    const workgroupsX = Math.ceil(graphicsConfig.shadowMapResolution / 256);

    // ── Pass 1: trace rays → raw nearest-occluder distance per angle ──
    ctx.setComputePipeline!(this.computePipeline);
    ctx.bindComputeResources!({
      uniforms: [this._uniformBuffer],
      storageBuffers: [
        { buffer: lightManager.lightBuffer },
        { buffer: this._edgeBuffer },
        { buffer: this._rawShadowBuffer },
        { buffer: this._shadowLightIndexBuffer },
      ],
    });
    ctx.dispatchCompute!({ x: workgroupsX, y: shadowLightCount });
    // Flush so the blur pass reads the committed raw distances (cross-dispatch read-after-write).
    ctx.flushCompute!();

    // ── Pass 2: Gaussian-blur raw distances → 2-moment (μ, E[d²]) buffer for single-tap soft shadows ──
    ctx.setComputePipeline!(this.blurPipeline);
    ctx.bindComputeResources!({
      uniforms: [this._uniformBuffer],
      storageBuffers: [
        { buffer: lightManager.lightBuffer },
        { buffer: this._rawShadowBuffer },
        { buffer: this._shadowMapBuffer },
        { buffer: this._shadowLightIndexBuffer },
      ],
    });
    ctx.dispatchCompute!({ x: workgroupsX, y: shadowLightCount });

    // Flush to make blurred shadow data available for fragment reads
    ctx.flushCompute!();
  }

  // -----------------------------------------------------------------------
  // Destroy
  // -----------------------------------------------------------------------

  /** Release all GPU resources. */
  destroy(): void {
    const ctx = this.ctx;
    if (this._uniformBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._uniformBuffer);
      this._uniformBuffer = NULL_HANDLE;
    }
    if (this._edgeBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._edgeBuffer);
      this._edgeBuffer = NULL_HANDLE;
    }
    if (this._rawShadowBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._rawShadowBuffer);
      this._rawShadowBuffer = NULL_HANDLE;
    }
    if (this._shadowMapBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._shadowMapBuffer);
      this._shadowMapBuffer = NULL_HANDLE;
    }
    if (this._shadowLightIndexBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._shadowLightIndexBuffer);
      this._shadowLightIndexBuffer = NULL_HANDLE;
    }
    if (this.computePipeline !== NULL_HANDLE) {
      ctx.destroyComputePipeline!(this.computePipeline);
      this.computePipeline = NULL_HANDLE;
    }
    if (this.computeShader !== NULL_HANDLE) {
      ctx.destroyShader(this.computeShader);
      this.computeShader = NULL_HANDLE;
    }
    if (this.blurPipeline !== NULL_HANDLE) {
      ctx.destroyComputePipeline!(this.blurPipeline);
      this.blurPipeline = NULL_HANDLE;
    }
    if (this.blurShader !== NULL_HANDLE) {
      ctx.destroyShader(this.blurShader);
      this.blurShader = NULL_HANDLE;
    }
  }
}
